/**
 * Atmosphere Component
 * Renders atmospheric glow around Earth using fresnel effect.
 * Two layers:
 *  - Outer (BackSide, scale 1.04): rim glow visible around Earth's silhouette
 *  - Inner (FrontSide, scale 1.001): subtle atmospheric haze on the Earth's surface
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { createAtmosphereMaterial, createInnerAtmosphereMaterial } from './materials/atmosphereMaterial'
import { DEFAULT_SPHERE_SEGMENTS } from './utilities/earthConstants'
import materialConfig from './config/materialConfig.json'

export function Atmosphere({
  radius = 2.5,
  segments = DEFAULT_SPHERE_SEGMENTS,
  rotation = [0, 0, 0],
  position = [0, 0, 0],
  sunPositionRef = null,
}) {
  const { gl } = useThree()
  const isWebGPU = gl.isWebGPURenderer || false
  const [material, setMaterial] = useState(null)
  const [innerMaterial, setInnerMaterial] = useState(null)
  const meshRef = useRef()
  const innerMeshRef = useRef()

  // Create geometry once using useMemo
  const atmosphereGeometry = useMemo(() => {
    return new THREE.SphereGeometry(radius, segments, segments)
  }, [radius, segments])

  // Cleanup geometry on unmount
  useEffect(() => {
    return () => {
      atmosphereGeometry.dispose()
    }
  }, [atmosphereGeometry])

  // Outer atmosphere material (BackSide rim glow)
  useEffect(() => {
    let isMounted = true
    let currentMaterial = null

    const config = {
      atmosphereDayColor: materialConfig.atmosphereDayColor,
      atmosphereTwilightColor: materialConfig.atmosphereTwilightColor,
      atmosphereFresnelStart: materialConfig.atmosphereFresnelStart,
      atmosphereFresnelPower: materialConfig.atmosphereFresnelPower,
      atmosphereIntensity: materialConfig.atmosphereIntensity,
    }

    createAtmosphereMaterial(isWebGPU, config).then((newMaterial) => {
      if (isMounted) {
        currentMaterial = newMaterial
        setMaterial((prevMaterial) => {
          if (prevMaterial && prevMaterial !== newMaterial) {
            prevMaterial.dispose()
          }
          return newMaterial
        })
      } else {
        newMaterial.dispose()
      }
    })

    return () => {
      isMounted = false
      if (currentMaterial) {
        currentMaterial.dispose()
      }
    }
  }, [isWebGPU])

  // Inner atmosphere material (FrontSide surface haze)
  useEffect(() => {
    let isMounted = true
    let currentMaterial = null

    const config = {
      atmosphereDayColor: materialConfig.atmosphereDayColor,
      atmosphereTwilightColor: materialConfig.atmosphereTwilightColor,
      innerAtmosphereFresnelPower: materialConfig.innerAtmosphereFresnelPower,
      innerAtmosphereIntensity: materialConfig.innerAtmosphereIntensity,
    }

    createInnerAtmosphereMaterial(isWebGPU, config).then((newMaterial) => {
      if (isMounted) {
        currentMaterial = newMaterial
        setInnerMaterial((prevMaterial) => {
          if (prevMaterial && prevMaterial !== newMaterial) {
            prevMaterial.dispose()
          }
          return newMaterial
        })
      } else {
        newMaterial.dispose()
      }
    })

    return () => {
      isMounted = false
      if (currentMaterial) {
        currentMaterial.dispose()
      }
    }
  }, [isWebGPU])
  
  // Update light position uniform and camera-distance fade every frame
  useFrame((state) => {
    const sp = sunPositionRef?.current

    // Update outer atmosphere
    if (material && material.userData.lightPositionUniformNode) {
      const uniformNode = material.userData.lightPositionUniformNode
      if (uniformNode.value && sp) {
        uniformNode.value.set(sp[0], sp[1], sp[2])
      }
    }

    // Update inner atmosphere
    if (innerMaterial && innerMaterial.userData.lightPositionUniformNode) {
      const uniformNode = innerMaterial.userData.lightPositionUniformNode
      if (uniformNode.value && sp) {
        uniformNode.value.set(sp[0], sp[1], sp[2])
      }
    }

    // Fade atmosphere in sync with Earth — fully visible at 3.0+, invisible at 1.8
    const camDist = state.camera.position.length()
    const fadeStart = 3.0
    const fadeEnd = 1.8
    const opacity = Math.max(0, Math.min(1, (camDist - fadeEnd) / (fadeStart - fadeEnd)))

    if (material?.userData?.opacityFadeUniform) {
      material.userData.opacityFadeUniform.value = opacity
    }
    if (innerMaterial?.userData?.opacityFadeUniform) {
      innerMaterial.userData.opacityFadeUniform.value = opacity
    }

    // Hide the outer atmosphere mesh entirely when fading — prevents BackSide faces
    // from appearing as a solid dark shape on the dark side of Earth
    if (meshRef.current) {
      meshRef.current.visible = opacity >= 0.99
    }
    if (innerMeshRef.current) {
      innerMeshRef.current.visible = opacity >= 0.99
    }
  })

  return (
    <>
      {/* Outer atmosphere — BackSide rim glow */}
      <mesh
        ref={meshRef}
        geometry={atmosphereGeometry}
        scale={1.04}
      >
        {material ? (
          <primitive object={material} attach="material" />
        ) : null}
      </mesh>

      {/* Inner atmosphere — FrontSide surface haze */}
      <mesh
        ref={innerMeshRef}
        geometry={atmosphereGeometry}
        scale={1.001}
      >
        {innerMaterial ? (
          <primitive object={innerMaterial} attach="material" />
        ) : null}
      </mesh>
    </>
  )
}

export default Atmosphere